/**
 * CognitoIdentityClient — implementação concreta do `CognitoClient` sobre o
 * `amazon-cognito-identity-js` (fluxo SRP nativo do Cognito, sem secret — app
 * client público). Roda 100% no browser (SPA pura); os tokens do SDK ficam na
 * `sessionStorage` da ABA (ADR-031: F5 mantém a sessão, fechar a aba encerra; nunca
 * `localStorage`) — memória se a aba não oferecer `sessionStorage`.
 *
 * Mapeia os erros do Cognito para mensagens GENÉRICAS (sem vazar existência de
 * conta nem detalhe de infra — RN-GLOBAL-004, padrão "Erros Genéricos"). Nenhum
 * dado pessoal é logado (RN-M11-001).
 */

import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';
import {
  CognitoClient,
  CognitoSession,
  MfaChallenge,
  NovaSenhaChallenge,
  SignInResult,
} from './cognitoClient';
import { CognitoRuntimeConfig } from '../config/runtime-config';

/** Erro de autenticação com mensagem já genérica (segura para exibir na UI). */
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

const MSG_CREDENCIAIS = 'Credenciais inválidas. Verifique e tente novamente.';
const MSG_MFA = 'Código de verificação inválido ou expirado.';
const MSG_GENERICO = 'Não foi possível concluir a operação. Tente novamente.';

/** Extrai a sessão tipada de um `CognitoUserSession`. */
function paraSessao(session: CognitoUserSession): CognitoSession {
  const accessToken = session.getAccessToken();
  return {
    accessToken: accessToken.getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    // getExpiration() é epoch em segundos; convertido para ms.
    expiresAt: accessToken.getExpiration() * 1000,
  };
}

const MFA_CHALLENGE: MfaChallenge = { challenge: 'MFA_TOTP' };
const NOVA_SENHA_CHALLENGE: NovaSenhaChallenge = { challenge: 'NOVA_SENHA' };
const MSG_NOVA_SENHA =
  'Não foi possível definir a nova senha. Verifique se ela atende à política de senha.';

/**
 * Armazenamento SÓ em memória (fallback do `SessaoAbaStorage`; nunca localStorage).
 * Sem um storage explícito, o `amazon-cognito-identity-js` grava os tokens no
 * `localStorage` do navegador por padrão.
 */
export class MemoriaStorage implements ICognitoStorage {
  private readonly dados = new Map<string, string>();
  setItem(key: string, value: string): void {
    this.dados.set(key, value);
  }
  getItem(key: string): string | null {
    return this.dados.get(key) ?? null;
  }
  removeItem(key: string): void {
    this.dados.delete(key);
  }
  clear(): void {
    this.dados.clear();
  }
}

/** Prefixo das chaves que o `amazon-cognito-identity-js` grava no storage. */
const PREFIXO_SDK = 'CognitoIdentityServiceProvider.';

/** `sessionStorage` da aba, se utilizável (modo privado restrito pode bloquear). */
function sessionStorageDaAba(): Storage | null {
  try {
    const s = window.sessionStorage;
    const teste = '__acervo_teste__';
    s.setItem(teste, '1');
    s.removeItem(teste);
    return s;
  } catch {
    return null;
  }
}

/**
 * Armazenamento do SDK na `sessionStorage` da ABA (ADR-031, emenda do Req 1 AC5): F5
 * restaura a sessão; fechar a aba encerra; nunca `localStorage`. Sem `sessionStorage`
 * (ou se ela falhar), usa memória — o comportamento anterior.
 */
export class SessaoAbaStorage implements ICognitoStorage {
  private readonly memoria = new MemoriaStorage();
  private readonly aba: Storage | null;

  constructor(aba: Storage | null = sessionStorageDaAba()) {
    this.aba = aba;
  }

  setItem(key: string, value: string): void {
    try {
      if (this.aba) {
        this.aba.setItem(key, value);
        return;
      }
    } catch {
      // cota/política do navegador: cai para memória
    }
    this.memoria.setItem(key, value);
  }

  getItem(key: string): string | null {
    try {
      const v = this.aba?.getItem(key);
      if (v !== null && v !== undefined) {
        return v;
      }
    } catch {
      // ignora: tenta a memória
    }
    return this.memoria.getItem(key);
  }

  removeItem(key: string): void {
    try {
      this.aba?.removeItem(key);
    } catch {
      // ignora
    }
    this.memoria.removeItem(key);
  }

  /** Limpa só as chaves do SDK (não a `sessionStorage` inteira da aba). */
  clear(): void {
    try {
      if (this.aba) {
        const chaves: string[] = [];
        for (let i = 0; i < this.aba.length; i += 1) {
          const k = this.aba.key(i);
          if (k && k.startsWith(PREFIXO_SDK)) {
            chaves.push(k);
          }
        }
        chaves.forEach((k) => this.aba?.removeItem(k));
      }
    } catch {
      // ignora
    }
    this.memoria.clear();
  }
}

export class CognitoIdentityClient implements CognitoClient {
  private readonly pool: CognitoUserPool;
  private readonly storage: ICognitoStorage;
  /** Usuário corrente do fluxo (guardado entre signIn e os desafios). */
  private currentUser: CognitoUser | null = null;

  constructor(config: CognitoRuntimeConfig, storage: ICognitoStorage = new SessaoAbaStorage()) {
    this.storage = storage;
    this.pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
      Storage: this.storage,
    });
  }

  private novoUsuario(email: string): CognitoUser {
    return new CognitoUser({ Username: email, Pool: this.pool, Storage: this.storage });
  }

  public signIn(email: string, password: string): Promise<SignInResult> {
    const user = this.novoUsuario(email);
    this.currentUser = user;
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<SignInResult>((resolve, reject) => {
      user.authenticateUser(authDetails, {
        onSuccess: (session) => resolve(paraSessao(session)),
        onFailure: () => reject(new AuthError(MSG_CREDENCIAIS)),
        // Primeiro acesso: senha temporária → a UI pede a nova senha.
        newPasswordRequired: () => resolve(NOVA_SENHA_CHALLENGE),
        // Desafio TOTP (RN-M1-001: sempre para admin). A UI então chama confirmMfa.
        totpRequired: () => resolve(MFA_CHALLENGE),
        // MFA por software token também cai aqui em algumas versões do fluxo.
        mfaRequired: () => resolve(MFA_CHALLENGE),
      });
    });
  }

  public completeNewPassword(newPassword: string): Promise<SignInResult> {
    const user = this.currentUser;
    if (!user) {
      return Promise.reject(new AuthError(MSG_GENERICO));
    }
    return new Promise<SignInResult>((resolve, reject) => {
      user.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: (session) => resolve(paraSessao(session)),
          onFailure: () => reject(new AuthError(MSG_NOVA_SENHA)),
          totpRequired: () => resolve(MFA_CHALLENGE),
          mfaRequired: () => resolve(MFA_CHALLENGE),
        },
      );
    });
  }

  public associarTotp(): Promise<string> {
    const user = this.currentUser;
    if (!user) {
      return Promise.reject(new AuthError(MSG_GENERICO));
    }
    return new Promise<string>((resolve, reject) => {
      user.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => resolve(secretCode),
        onFailure: () => reject(new AuthError(MSG_GENERICO)),
      });
    });
  }

  public verificarTotp(code: string): Promise<void> {
    const user = this.currentUser;
    if (!user) {
      return Promise.reject(new AuthError(MSG_GENERICO));
    }
    return new Promise<void>((resolve, reject) => {
      user.verifySoftwareToken(code, 'Autenticador', {
        onSuccess: () => {
          // TOTP verificado: ativa como fator preferido para os próximos logins.
          user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
            if (err) {
              reject(new AuthError(MSG_GENERICO));
              return;
            }
            resolve();
          });
        },
        onFailure: () => reject(new AuthError(MSG_MFA)),
      });
    });
  }

  public confirmMfa(code: string): Promise<CognitoSession> {
    const user = this.currentUser;
    if (!user) {
      return Promise.reject(new AuthError(MSG_GENERICO));
    }
    return new Promise<CognitoSession>((resolve, reject) => {
      user.sendMFACode(
        code,
        {
          onSuccess: (session) => resolve(paraSessao(session)),
          onFailure: () => reject(new AuthError(MSG_MFA)),
        },
        'SOFTWARE_TOKEN_MFA',
      );
    });
  }

  public forgotPassword(email: string): Promise<void> {
    const user = this.novoUsuario(email);
    return new Promise<void>((resolve, reject) => {
      user.forgotPassword({
        // Sucesso quando o código é enviado (resposta neutra — não revela conta).
        onSuccess: () => resolve(),
        inputVerificationCode: () => resolve(),
        onFailure: () => reject(new AuthError(MSG_GENERICO)),
      });
    });
  }

  public confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    const user = this.novoUsuario(email);
    return new Promise<void>((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: () => reject(new AuthError(MSG_GENERICO)),
      });
    });
  }

  public signOut(): Promise<void> {
    const user = this.currentUser ?? this.pool.getCurrentUser();
    if (user) {
      user.signOut();
    }
    this.currentUser = null;
    // Garante que nenhum token do SDK fica na aba após sair (ADR-031).
    this.storage.clear();
    return Promise.resolve();
  }

  /**
   * Sessão corrente da aba. Sem `forcar`, o SDK devolve a sessão guardada e, se o id
   * token venceu, já renova pelo refresh token (restauração após F5). Com `forcar`,
   * renova mesmo com o token ainda válido (renovação proativa antes de vencer).
   * `null` = sem sessão ou refresh inválido (8 h vencidas, usuário desativado).
   */
  public refresh(forcar = false): Promise<CognitoSession | null> {
    const user = this.currentUser ?? this.pool.getCurrentUser();
    if (!user) {
      return Promise.resolve(null);
    }
    return new Promise<CognitoSession | null>((resolve) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        this.currentUser = user;
        if (!forcar) {
          resolve(paraSessao(session));
          return;
        }
        user.refreshSession(session.getRefreshToken(), (erro: Error | null, nova: CognitoUserSession | null) => {
          resolve(erro || !nova ? null : paraSessao(nova));
        });
      });
    });
  }
}
